import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as ActionTypes from "../actions/actionTypes";
import { getCustomerZoneBarData } from "../actions/dashboardActions";
import DataTable from "react-data-table-component";
import Spinner from "../components/Spinner";
import { useTranslation } from "react-i18next";
import { utils } from "../common/utils";
import { Breakpoint } from "react-socks";


export default function CustomerZoneBarData({ zoneId }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const user = useSelector((state) => state.user);
  const { items, totalCount, loading } = useSelector(
  (state) =>
    state.dashboard.customerZoneBarData || {
      items: [],
      totalCount: 0,
      loading: false
    }
);

  useEffect(() => {
    if (zoneId) {
      dispatch({type: ActionTypes.RESET_CUSTOMER_ZONE_BAR_DATA,});
      dispatch(getCustomerZoneBarData(user.id,zoneId, currentPage, perPage));
    }
  }, [user.id,zoneId, currentPage, perPage, dispatch]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const columns = [
    { name: <span title={t("StockCode")}>{t("StockCode")}</span>, selector: (row) => row.stockItem?.stockCode, sortable: true, wrap: true },
    { name: <span title={t("StockName")}>{t("StockName")}</span>, selector: (row) => row.stockItem?.name, sortable: true, wrap: true },
    { name: <span title={t("ZoneCode")}>{t("ZoneCode")}</span>, selector: (row) => row.zoneCode, sortable: true, wrap: true },
    { name: <span title={t("Quantity")}>{t("Quantity")}</span>, selector: (row) => row.quantity, sortable: true, wrap: true },
    { name: <span title={t("TotalPoints")}>{t("TotalPoints")}</span>, selector: (row) => row.totalPoints, sortable: true, wrap: true },
    { name: <span title={t("SoldAt")}>{t("SoldAt")}</span>, selector: (row) => utils.dates.getDateTime24String(row.soldAt), sortable: true, wrap: true },
    { name: <span title={t("OrderId")}>{t("OrderId")}</span>, selector: (row) => row.orderId, sortable: true, wrap: true },
    { name: <span title={t("OrderedAt")}>{t("OrderedAt")}</span>, selector: (row) => utils.dates.getDateTime24String(row.orderedAt), sortable: true, wrap: true },
    { name: <span title={t("ExpiryDate")}>{t("ExpiryDate")}</span>, selector: (row) => utils.dates.getDateString(row.expiryDateFromOrderDate), sortable: true, wrap: true },
    { name: <span title={t("ConsultantFullName")}>{t("ConsultantFullName")}</span>, selector: (row) => row.consultant?.fullName, sortable: true, wrap: true },
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: "var(--theme-bg-surface)",
        color: "var(--theme-text-primary)",
      },
    },
    headRow: { style: { minHeight: "44px", backgroundColor: "var(--theme-bg-elevated)", borderBottom: "2px solid var(--theme-border-soft)" } },
    headCells: { style: { padding: "8px 12px", fontSize: "14px", fontWeight: "600", color: "var(--theme-text-primary)", whiteSpace: "nowrap", overflow: "visible", textOverflow: "unset" } },
    rows: {
      style: {
        minHeight: "40px",
        backgroundColor: "var(--theme-bg-surface)",
        color: "var(--theme-text-secondary)",
        borderBottomColor: "var(--theme-border-soft)",
      },
      stripedStyle: {
        backgroundColor: "var(--theme-bg-elevated)",
      },
      highlightOnHoverStyle: {
        backgroundColor: "var(--theme-bg-elevated)",
        color: "var(--theme-text-primary)",
        outline: "none",
      },
    },
    cells: { style: { fontSize: "14px", color: "var(--theme-text-secondary)", padding: "8px 12px" } },
    pagination: {
      style: {
        backgroundColor: "var(--theme-bg-secondary)",
        color: "var(--theme-text-secondary)",
        borderTop: "1px solid var(--theme-border-soft)",
      },
      pageButtonsStyle: {
        color: "var(--theme-text-secondary)",
        fill: "var(--theme-text-secondary)",
        borderRadius: "50%",
        "&:disabled": {
          color: "var(--theme-text-muted)",
          fill: "var(--theme-text-muted)",
        },
        "&:hover:not(:disabled)": {
          backgroundColor: "var(--theme-bg-elevated)",
        },
        "&:focus": {
          outline: "none",
          backgroundColor: "var(--theme-bg-elevated)",
        },
      },
    },
    noData: {
      style: {
        backgroundColor: "var(--theme-bg-surface)",
        color: "var(--theme-text-secondary)",
      },
    },
  };
  const loaderComponent = (
    <div style={{width: "300px",height: "100px",margin: "20px auto",display: "flex",justifyContent: "center", alignItems: "center" }} >
      <Spinner showLoader={true} />
    </div>
  );

  const MobileView = ({ items }) => (
      <div className="mobile-accordion">
        {items.map((row, i) => (
          <details
            key={i}
            style={{border: "1px solid var(--theme-border-soft)", borderRadius: "8px",marginBottom: "8px",padding: "8px",background: "var(--theme-bg-surface)", color: "var(--theme-text-secondary)" }}
          >
            <summary style={{fontWeight: "700", cursor: "pointer",color: "var(--theme-text-primary)"}}>
              {row.stockItem?.name}
            </summary>

            <div style={{ marginTop: "8px", fontSize: "14px", color: "var(--theme-text-secondary)" }}>
              <p><b>{t("stockCode")}:</b> {row.stockItem?.stockCode}</p>
              <p><b>{t("ZoneCode")}:</b> {row.zoneCode}</p>
              <p><b>{t("Quantity")}:</b> {row.quantity}</p>
              <p><b>{t("TotalPoints")}:</b> {row.totalPoints}</p>
              <p><b>{t("SoldAt")}:</b> {utils.dates.getDateTime24String(row.soldAt)}</p>
              <p><b>{t("OrderId")}:</b> {row.orderId}</p>
              <p><b>{t("OrderedAt")}:</b> {utils.dates.getDateTime24String(row.orderedAt)}</p>
              <p><b>{t("ExpiryDate")}:</b> {utils.dates.getDateString(row.expiryDateFromOrderDate)}</p>
              <p><b>{t("ConsultantFullName")}:</b> {row.consultant?.fullName}</p>
            </div>
          </details>
        ))}
      </div>
    );
  return (
    <div style={{ padding: "16px", position: "relative" }}>
      <Breakpoint medium up>
      <DataTable
        columns={columns}
        data={items}
        customStyles={customStyles}   
        pagination
        paginationServer
        paginationTotalRows={totalCount}
        paginationPerPage={perPage}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        highlightOnHover
        striped
        responsive
        paginationComponentOptions={{
          rowsPerPageText: t("RowsPerPage"),
          rangeSeparatorText: t("of"),
          selectAllRowsItemText: t("All"),
        }}
        progressPending={loading}               
        progressComponent={loaderComponent}
        noDataComponent={<div style={{ padding: "16px", color: "var(--theme-text-secondary)" }}>{t("There are no records to display")}</div>}
      />
      </Breakpoint>

      <Breakpoint small down>
        {loading ? (loaderComponent) : items.length > 0 ? ( <MobileView items={items} />) : (
          <div style={{ padding: "16px", color: "var(--theme-text-secondary)" }}>{t("There are no records to display")}</div>
        )}
      </Breakpoint>
    </div>
  );
}
